import React, { useState, useEffect, useCallback } from 'react';
import { 
    Table, 
    Tabs, 
    Button, 
    Tag, 
    Space, 
    Modal, 
    Input, 
    Typography, 
    notification,
    Card,
    Tooltip
} from 'antd';
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    EditOutlined, 
    CloudSyncOutlined,
    BulbOutlined,
    BookOutlined
} from '@ant-design/icons';
import { fetchRagEntries, updateRagEntry } from '../../apis/crmApi';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Title, Text } = Typography;

const KnowledgeBase = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('PENDING');
    const [editModal, setEditModal] = useState({ visible: false, entry: null });
    const [editAnswer, setEditAnswer] = useState('');
    const [spinning, setSpinning] = useState(false);

    const loadEntries = useCallback(async (currentStatus) => {
        setLoading(true);
        try {
            const res = await fetchRagEntries(currentStatus);
            setEntries(res.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'No se pudieron cargar las entradas de conocimiento.',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEntries(status);
    }, [status, loadEntries]);

    const handleStatusUpdate = async (id, newStatus, finalAnswer = null) => {
        setSpinning(true);
        setLoading(true); // Mostrar spinner en la tabla
        try {
            const payload = { status: newStatus };
            if (finalAnswer) payload.answer = finalAnswer;

            await updateRagEntry(id, payload);
            
            notification.success({
                message: newStatus === 'APPROVED' ? '¡Conocimiento Indexado!' : 'Estado Actualizado',
                description: newStatus === 'APPROVED' 
                    ? 'La respuesta ha sido sincronizada con Pinecone y el bot ya puede usarla.' 
                    : `La entrada ha sido marcada como ${newStatus.toLowerCase()}.`,
                icon: newStatus === 'APPROVED' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null
            });

            await loadEntries(status);
            setEditModal({ visible: false, entry: null });
        } catch (error) {
            notification.error({
                message: 'Error de Sincronización',
                description: 'Hubo un problema al actualizar el estado o indexar en Pinecone.',
            });
        } finally {
            setSpinning(false);
            setLoading(false);
        }
    };

    const openEditModal = (entry) => {
        setEditModal({ visible: true, entry });
        setEditAnswer(entry.answer || '');
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            fixed: 'left',
            render: (id) => <Text type="secondary">#{id}</Text>
        },
        {
            title: 'Pregunta / Concepto',
            dataIndex: 'question',
            key: 'question',
            minWidth: 250,
            render: (text) => <div style={{ minWidth: '200px' }}><Text strong>{text}</Text></div>
        },
        {
            title: 'Respuesta Sugerida',
            dataIndex: 'answer',
            key: 'answer',
            minWidth: 300,
            render: (text) => (
                <div style={{ minWidth: '250px' }}>
                    {text ? <Text>{text}</Text> : <Text italic type="danger">Sin respuesta</Text>}
                </div>
            )
        },
        {
            title: 'Fuente',
            dataIndex: 'source',
            key: 'source',
            width: 160,
            render: (source) => (
                <Tag color={source === 'human_feedback' ? 'gold' : 'blue'} style={{ margin: 0 }}>
                    {source === 'human_feedback' ? <BulbOutlined /> : null} {source.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Creado',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="middle">
                    {status === 'PENDING' ? (
                        <>
                            <Button 
                                type="primary" 
                                shape="circle" 
                                icon={<EditOutlined />} 
                                onClick={() => openEditModal(record)} 
                            />
                            <Button 
                                danger 
                                shape="circle" 
                                icon={<CloseCircleOutlined />} 
                                onClick={() => handleStatusUpdate(record.id, 'REJECTED')} 
                            />
                        </>
                    ) : (
                        <Button 
                            icon={<CloudSyncOutlined />} 
                            onClick={() => handleStatusUpdate(record.id, 'PENDING')} 
                        />
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="knowledge-base-container" style={{ padding: 'clamp(8px, 2vw, 24px)', background: '#f0f2f5', minHeight: '100vh' }}>
            <Card 
                className="main-card"
                style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}
                bodyStyle={{ padding: 'clamp(12px, 3vw, 24px)' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                    <BookOutlined style={{ fontSize: 'clamp(24px, 5vw, 32px)', color: '#1890ff' }} />
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <Title level={2} style={{ margin: 0, fontSize: 'clamp(18px, 4vw, 24px)' }}>Gestión de Conocimiento</Title>
                        <Text type="secondary">Triage de RAG: Aprueba feedback humano para entrenar al bot.</Text>
                    </div>
                </div>

                <Tabs 
                    activeKey={status} 
                    onChange={setStatus} 
                    type="card" 
                    style={{ marginBottom: '16px' }}
                >
                    <TabPane 
                        tab={<span>Pendientes {status === 'PENDING' && <Tag style={{ marginLeft: 8 }}>{entries.length}</Tag>}</span>} 
                        key="PENDING" 
                    />
                    <TabPane tab="Aprobados" key="APPROVED" />
                    <TabPane tab="Rechazados" key="REJECTED" />
                </Tabs>

                <div className="table-wrapper">
                    <Table 
                        columns={columns} 
                        dataSource={entries} 
                        rowKey="id" 
                        loading={loading}
                        pagination={{ pageSize: 15 }}
                        scroll={{ x: 1000 }} // Habilita scroll horizontal en desktop si es necesario
                        locale={{ emptyText: 'No hay entradas para este estado.' }}
                        className="custom-rag-table"
                    />
                </div>
            </Card>

            <Modal
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <span>Validar Conocimiento</span>
                    </Space>
                }
                visible={editModal.visible}
                onOk={() => handleStatusUpdate(editModal.entry.id, 'APPROVED', editAnswer)}
                onCancel={() => setEditModal({ visible: false, entry: null })}
                confirmLoading={spinning}
                okText="Aprobar e Indexar"
                cancelText="Cancelar"
                width={700}
                centered
                bodyStyle={{ padding: 'clamp(12px, 4vw, 24px)' }}
            >
                {editModal.entry && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Text type="secondary">Pregunta del Usuario:</Text>
                            <Card size="small" style={{ background: '#f5f5f5', border: 'none', marginTop: '4px' }}>
                                <Text strong>{editModal.entry.question}</Text>
                            </Card>
                        </div>
                        <div>
                            <Text type="secondary">Respuesta del Bot (Edita si es necesario):</Text>
                            <TextArea 
                                rows={6} 
                                value={editAnswer} 
                                onChange={(e) => setEditAnswer(e.target.value)}
                                style={{ marginTop: '4px', borderRadius: '8px' }}
                                placeholder="Escribe la respuesta final que el bot usará para esta pregunta..."
                            />
                        </div>
                        <notification type="info">
                            <Text size="small" type="secondary">
                                Al aprobar, esta entrada será procesada por Pinecone. Los vectores resultantes estarán disponibles de inmediato para el motor de búsqueda semántica.
                            </Text>
                        </notification>
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default KnowledgeBase;
